/**
 * PaymentScheduleTemplate `params` — SCRUM-291 (DS-021 §3.5, Payments BC slice 3).
 *
 * The single source of truth (CLAUDE.md rule 3) for the shape of a payment
 * schedule template's `params` field. A template's `params` are discriminated by
 * `breakdownKind` into three guide configs (DS-021 §3.5 / §6.5):
 *
 *   - **PaidInFull**  — one installment at booking; optional `arrivalWindowDays`.
 *   - **TwoStep**     — a deposit (% / fixed / 1-night) at booking, then the
 *                       residual balance `daysBeforeArrival`.
 *   - **MultiPayment** — deposit + intermediate installment(s) + a final residual
 *                       installment before the check-in cutoff (≥ 2 installments).
 *
 * Design notes:
 *   - The validators package owns this shape; the payments domain
 *     (`payment-schedule-template.entity.ts` / `payment-schedule.vo.ts`) enforces
 *     the same constraints at the aggregate boundary and layers the
 *     non-structural invariants the wire schema cannot express (percentage splits
 *     summing to 100%, the final installment being the residual that absorbs
 *     rounding via `applyBasisPoints` ROUND_HALF_UP). DO NOT import domain code
 *     here, and DO NOT redefine this shape anywhere else.
 *   - **No currency field.** Currency is resolved to the org default at freeze and
 *     lives on the `Folio` row (one folio = one currency), not on the template;
 *     `currencyCodeSchema` is therefore intentionally NOT referenced here.
 *   - **Money is bp / Cents.** Splits are integer basis points in `[0, 10000]`;
 *     fixed amounts are non-negative integer `Cents`. No native float arithmetic.
 *   - Every object schema is `.strict()` so the discriminated union rejects
 *     cross-kind param mixing (e.g. TwoStep scalars submitted under `PaidInFull`)
 *     and any unknown key.
 *   - `kind` and `event` are closed enums — they reject wildcards, trailing
 *     separators, SQL/template/glob/shell-shaped strings, and any other free-form
 *     string by construction.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Closed enums (reject wildcards / injection-shaped / trailing-separator strings)
// ---------------------------------------------------------------------------
/** Discriminant for a template's params. */
export const BREAKDOWN_KIND_VALUES = ['PaidInFull', 'TwoStep', 'MultiPayment'];
/** Installment role within a MultiPayment schedule. */
export const INSTALLMENT_KIND_VALUES = ['deposit', 'intermediate', 'balance'];
/** When an installment's trigger fires. */
export const TRIGGER_EVENT_VALUES = ['at-booking', 'days-before-arrival'];
// ---------------------------------------------------------------------------
// Reusable numeric guards (non-negative integers; bp clamp 0..10000)
// ---------------------------------------------------------------------------
/** Non-negative integer count of days (offsets / windows). */
const nonNegativeIntDays = z.number().int().min(0);
/** Integer basis points in the inclusive range [0, 10000] (0%..100%). */
const basisPoints0to10000 = z.number().int().min(0).max(10_000);
/** Non-negative integer `Cents`. */
const nonNegativeCents = z.number().int().min(0);
// ---------------------------------------------------------------------------
// Installment trigger — discriminated by `event`
// ---------------------------------------------------------------------------
const templateTriggerAtBookingSchema = z
    .object({
    event: z.literal('at-booking'),
})
    .strict();
const templateTriggerDaysBeforeArrivalSchema = z
    .object({
    event: z.literal('days-before-arrival'),
    /** Days before arrival the trigger fires (non-negative integer; 0 = check-in day). */
    offsetDays: nonNegativeIntDays,
})
    .strict();
const templateTriggerSchema = z.discriminatedUnion('event', [
    templateTriggerAtBookingSchema,
    templateTriggerDaysBeforeArrivalSchema,
]);
// ---------------------------------------------------------------------------
// Installment amount — percentBasisPoints | fixedCents | residual
// ---------------------------------------------------------------------------
const templateAmountPercentSchema = z
    .object({
    /** Share of the total, in integer basis points (0..10000 = 0%..100%). */
    percentBasisPoints: basisPoints0to10000,
})
    .strict();
const templateAmountFixedSchema = z
    .object({
    /** Fixed amount in non-negative integer `Cents`. */
    fixedCents: nonNegativeCents,
})
    .strict();
const templateAmountResidualSchema = z
    .object({
    /** Marks the installment that absorbs the rounding remainder (sums to total). */
    residual: z.literal(true),
})
    .strict();
const templateAmountSchema = z.union([
    templateAmountPercentSchema,
    templateAmountFixedSchema,
    templateAmountResidualSchema,
]);
// ---------------------------------------------------------------------------
// A single MultiPayment installment
// ---------------------------------------------------------------------------
const templateInstallmentSchema = z
    .object({
    /** 1-based ordinal of this milestone within the schedule. */
    sequence: z.number().int().min(1),
    /** Installment role (closed set). */
    kind: z.enum(INSTALLMENT_KIND_VALUES),
    /** When this installment becomes due. */
    trigger: templateTriggerSchema,
    /** How much this installment collects. */
    amount: templateAmountSchema,
})
    .strict();
// ---------------------------------------------------------------------------
// Per-kind params schemas
// ---------------------------------------------------------------------------
/** PaidInFull — one collection at booking; optional pre-arrival reminder window. */
export const paidInFullParamsSchema = z
    .object({
    breakdownKind: z.literal('PaidInFull'),
    /** Optional days-before-arrival reminder window (non-negative integer). */
    arrivalWindowDays: nonNegativeIntDays.optional(),
})
    .strict();
/** TwoStep — a deposit at booking, residual balance `daysBeforeArrival`. */
export const twoStepParamsSchema = z
    .object({
    breakdownKind: z.literal('TwoStep'),
    /** Deposit share in integer basis points (0..10000). */
    depositPercent: basisPoints0to10000,
    /** Days after booking the deposit is collected (non-negative integer). */
    depositDaysAfterBooking: nonNegativeIntDays,
    /** Days before arrival the residual balance is collected (non-negative integer). */
    balanceDaysBeforeArrival: nonNegativeIntDays,
})
    .strict();
/** MultiPayment — deposit + intermediate(s) + residual; at least 2 installments. */
export const multiPaymentParamsSchema = z
    .object({
    breakdownKind: z.literal('MultiPayment'),
    /** Ordered installments (length ≥ 2: at minimum a deposit + a residual). */
    installments: z.array(templateInstallmentSchema).min(2),
})
    .strict();
// ---------------------------------------------------------------------------
// Discriminated union (by `breakdownKind`)
// ---------------------------------------------------------------------------
export const paymentScheduleTemplateParamsSchema = z.discriminatedUnion('breakdownKind', [
    paidInFullParamsSchema,
    twoStepParamsSchema,
    multiPaymentParamsSchema,
]);
//# sourceMappingURL=payment-schedule-template-params.js.map